import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'
import * as StompJs from '@stomp/stompjs'
import { AuthenticationService } from '@/services/AuthenticationService'
import { onUnmounted, ref, watch, type Ref, unref } from 'vue'
import { useCallStore } from '@/stores/call'
import { getConsultationActivityHeartbeatInterval } from '@/global/environmentGetters'

export type ConsultationType = 'SCHEDULED' | 'DIRECT'

export function useConsultationActivity(
  isCallFound: Ref<boolean>,
  consultationType: ConsultationType | Ref<ConsultationType>
) {
  const { user } = storeToRefs(useUserStore())
  const { callConsultationRequestId, isInCall } = storeToRefs(useCallStore())

  const stompClient = ref<StompJs.Client | null>(null)
  const isHeartbeatActive = ref(false)

  const BROKER_URL = '/api/consultation-participant-activity'
  const JOIN_HEARTBEAT_DESTINATION = '/join'
  const HEARTBEAT_DESTINATION = '/heartbeat'

  function constructHeartbeatBody() {
    return JSON.stringify({
      consultationId: callConsultationRequestId.value,
      participantEmail: user.value?.email,
      consultationType: unref(consultationType)
    })
  }

  watch([isCallFound, isInCall], () => {
    if (isCallFound.value && isInCall.value) {
      handleHeartbeatConnection()
    } else {
      handleHeartbeatDisconnect()
    }
  })

  onUnmounted(() => {
    handleHeartbeatDisconnect()
  })

  async function handleHeartbeatConnection() {
    const accessToken = await AuthenticationService.updateAccessToken()

    const client = new StompJs.Client({
      brokerURL: BROKER_URL,
      connectHeaders: {
        Authorization: `Bearer ${accessToken}`
      }
    })

    stompClient.value = client

    client.onConnect = () => {
      isHeartbeatActive.value = true

      client.publish({
        destination: JOIN_HEARTBEAT_DESTINATION,
        body: constructHeartbeatBody()
      })
    }

    client.onWebSocketError = () => {
      isHeartbeatActive.value = false
    }

    client.onStompError = () => {
      isHeartbeatActive.value = false
    }

    client.activate()

    initializeHeartbeat()
  }

  function handleHeartbeatDisconnect() {
    clearHeartbeatInterval()
    stompClient.value?.deactivate()
  }

  const intervalId = ref<ReturnType<typeof setInterval> | null>(null)

  function initializeHeartbeat() {
    intervalId.value = setInterval(() => {
      stompClient.value?.publish({
        destination: HEARTBEAT_DESTINATION,
        body: constructHeartbeatBody()
      })
    }, getConsultationActivityHeartbeatInterval())
  }

  function clearHeartbeatInterval() {
    if (intervalId.value) {
      clearInterval(intervalId.value)
      intervalId.value = null
    }
    isHeartbeatActive.value = false
  }

  return {
    isHeartbeatActive
  }
}
